/**
 * Front-end deadhead and ETA calculation (Haversine only, no GIS).
 * Used after manual trip moves so only affected driver columns get recomputed.
 * Matches planner logic: first trip from driver start, next trips from previous dropoff.
 */

const EARTH_RADIUS_MILES = 3958.8;
const AVERAGE_SPEED_MPH = 35;
const MINUTES_PER_MILE = 60 / AVERAGE_SPEED_MPH;

/**
 * Haversine distance between two points in miles.
 * @param {number} lat1
 * @param {number} lon1
 * @param {number} lat2
 * @param {number} lon2
 * @returns {number} distance in miles
 */
export function haversineDistance(lat1, lon1, lat2, lon2) {
  if (lat1 == null || lon1 == null || lat2 == null || lon2 == null) return 0;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const lat1R = toRad(lat1);
  const lat2R = toRad(lat2);
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1R) * Math.cos(lat2R) * Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return EARTH_RADIUS_MILES * c;
}

/**
 * ETA in minutes from distance (miles). No GIS; uses same minutes-per-mile as planner fallback.
 * @param {number} distanceMiles
 * @returns {number} minutes
 */
export function distanceToEtaMinutes(distanceMiles) {
  if (distanceMiles == null || Number.isNaN(distanceMiles)) return 0;
  return distanceMiles * MINUTES_PER_MILE;
}

function num(v) {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
}

function fromLatLng(obj) {
  if (!obj) return null;
  const lat = num(obj.latitude ?? obj.lat ?? obj.Latitude);
  const lng = num(obj.longitude ?? obj.lng ?? obj.lon ?? obj.Longitude);
  if (lat == null || lng == null) return null;
  return { lat: lat, lng: lng };
}

function fromArray(arr) {
  if (!Array.isArray(arr) || arr.length < 2) return null;
  const a = num(arr[0]);
  const b = num(arr[1]);
  if (a == null || b == null) return null;
  const absA = Math.abs(a);
  const absB = Math.abs(b);
  const lat = absA <= 90 && absB > 90 ? a : absB <= 90 && absA > 90 ? b : a;
  const lng = absA <= 90 && absB > 90 ? b : absB <= 90 && absA > 90 ? a : b;
  return { lat: lat, lng: lng };
}

/**
 * Get pickup and dropoff coordinates from a trip object (supports multiple field names and shapes).
 * @param {object} trip
 * @returns {{ pickupLat: number, pickupLng: number, dropoffLat: number, dropoffLng: number } | null}
 */
export function getTripCoords(trip) {
  if (!trip) return null;

  let pickupLat =
    trip.pickup_lat ??
    trip.pickup_latitude ??
    trip.jobOriginLatitude ??
    trip.job_origin_latitude ??
    trip.origin?.latitude ??
    trip.origin?.lat ??
    trip.jobOrigin?.latitude ??
    trip.jobOrigin?.lat ??
    trip.job?.origin?.latitude ??
    trip.job?.origin?.lat;
  let pickupLng =
    trip.pickup_lng ??
    trip.pickup_longitude ??
    trip.jobOriginLongitude ??
    trip.job_origin_longitude ??
    trip.origin?.longitude ??
    trip.origin?.lng ??
    trip.origin?.lon ??
    trip.jobOrigin?.longitude ??
    trip.jobOrigin?.lng ??
    trip.job?.origin?.longitude ??
    trip.job?.origin?.lng;
  let dropoffLat =
    trip.dropoff_lat ??
    trip.dropoff_latitude ??
    trip.jobDestinationLatitude ??
    trip.job_destination_latitude ??
    trip.destination?.latitude ??
    trip.destination?.lat ??
    trip.jobDestination?.latitude ??
    trip.jobDestination?.lat ??
    trip.job?.destination?.latitude ??
    trip.job?.destination?.lat;
  let dropoffLng =
    trip.dropoff_lng ??
    trip.dropoff_longitude ??
    trip.jobDestinationLongitude ??
    trip.job_destination_longitude ??
    trip.destination?.longitude ??
    trip.destination?.lng ??
    trip.destination?.lon ??
    trip.jobDestination?.longitude ??
    trip.jobDestination?.lng ??
    trip.job?.destination?.longitude ??
    trip.job?.destination?.lng;

  if (pickupLat == null || pickupLng == null) {
    const po = fromLatLng(trip.origin) ?? fromLatLng(trip.jobOrigin) ?? fromLatLng(trip.pickupLocation) ?? fromLatLng(trip.job?.origin);
    if (po) {
      pickupLat = po.lat;
      pickupLng = po.lng;
    }
  }
  if ((pickupLat == null || pickupLng == null) && Array.isArray(trip.jobOrigin)) {
    const po = fromArray(trip.jobOrigin);
    if (po) {
      pickupLat = po.lat;
      pickupLng = po.lng;
    }
  }

  if (dropoffLat == null || dropoffLng == null) {
    const dest = fromLatLng(trip.destination) ?? fromLatLng(trip.jobDestination) ?? fromLatLng(trip.dropoffLocation) ?? fromLatLng(trip.job?.destination);
    if (dest) {
      dropoffLat = dest.lat;
      dropoffLng = dest.lng;
    }
  }
  if ((dropoffLat == null || dropoffLng == null) && Array.isArray(trip.jobDestination)) {
    const dest = fromArray(trip.jobDestination);
    if (dest) {
      dropoffLat = dest.lat;
      dropoffLng = dest.lng;
    }
  }

  const plat = num(pickupLat);
  const plng = num(pickupLng);
  const dlat = num(dropoffLat);
  const dlng = num(dropoffLng);
  if (plat == null || plng == null || dlat == null || dlng == null) return null;
  return {
    pickupLat: plat,
    pickupLng: plng,
    dropoffLat: dlat,
    dropoffLng: dlng,
  };
}

/**
 * Recompute deadhead_miles and eta_to_pickup_minutes for a driver's trips in order.
 * - First trip: from driver start (ref_latitude/ref_longitude or current_latitude/current_longitude) to pickup.
 * - For drivers with In Progress trip: ref = dropoff of in-progress (available_location), NOT current GPS.
 * - Next trips: from previous trip's dropoff to this trip's pickup.
 * - MULTILOAD TRIPS: For trips in same multiload group, card deadhead is P1→P2→P3 (not D1→P2).
 *   Only the first pickup counts toward driver total deadhead (see sumDeadheadMilesForTotal).
 * Skips trips that have no valid coords (e.g. is_break) or are is_busy_trip; leaves their deadhead/ETA unchanged.
 * For is_busy_trip we still update ref to its dropoff so the next trip's deadhead is correct.
 * @param {object} driver - Driver object with trips array and ref/current coordinates
 * @param {object} [multiloadRoutes] - Optional map: group_id -> [{trip_id, stop_type, sequence}, ...]
 * @returns {object[]} New trips array with updated deadhead_miles and eta_to_pickup_minutes (same objects mutated + returned for convenience)
 */
export function recomputeDeadheadAndEtaForDriver(driver, multiloadRoutes = null) {
  const trips = driver?.trips || [];
  if (!trips.length) return trips;

  // Use ref (dropoff for busy drivers) - backend sets ref_latitude/longitude = available_location for busy
  let refLat = driver.ref_latitude ?? driver.current_latitude;
  let refLng = driver.ref_longitude ?? driver.current_longitude;
  if (refLat == null || refLng == null) {
    refLat = 0;
    refLng = 0;
  }

  const sorted = [...trips].sort(
    (a, b) => (a.sequence ?? 999) - (b.sequence ?? 999)
  );

  // Build multiload group map (group_id -> list of trips in PICKUP order when available)
  const multiloadGroups = {};
  const groupsUnordered = {};
  for (const trip of sorted) {
    if (trip.is_multiload && trip.multiload_group_id) {
      const gid = trip.multiload_group_id;
      if (!groupsUnordered[gid]) groupsUnordered[gid] = [];
      groupsUnordered[gid].push(trip);
    }
  }

  const getKey = (t) => String(t?.trip_id || t?.id || '');

  for (const gid of Object.keys(groupsUnordered)) {
    const list = groupsUnordered[gid] || [];
    // 1) Best: multiload_pickup_order (backend-provided, stable)
    const hasPickupOrder = list.some((t) => t.multiload_pickup_order != null);
    if (hasPickupOrder) {
      multiloadGroups[gid] = [...list].sort(
        (a, b) => (Number(a.multiload_pickup_order) || 999) - (Number(b.multiload_pickup_order) || 999)
      );
      continue;
    }

    // 2) Next: derive pickup order from multiloadRoutes[gid]
    const steps = multiloadRoutes ? multiloadRoutes[gid] : null;
    if (Array.isArray(steps) && steps.length) {
      const pickupIds = [];
      for (const s of steps) {
        if (s?.stop_type === 'pickup' && s.trip_id != null) pickupIds.push(String(s.trip_id));
      }
      if (pickupIds.length) {
        const used = new Set();
        const ordered = [];
        for (const tid of pickupIds) {
          const match = list.find((t) => getKey(t) === tid);
          if (match) {
            ordered.push(match);
            used.add(getKey(match));
          }
        }
        for (const t of list) {
          const k = getKey(t);
          if (!used.has(k)) ordered.push(t);
        }
        multiloadGroups[gid] = ordered;
        continue;
      }
    }

    // 3) Fallback: current behavior (sequence order / column order)
    multiloadGroups[gid] = list;
  }

  for (const trip of sorted) {
    if (trip.is_break) continue;
    const coords = getTripCoords(trip);
    if (!coords) continue;

    // In Progress trips: skip recalc (they show "Available in"), but update ref to dropoff for next trip
    if (trip.is_busy_trip === true) {
      refLat = coords.dropoffLat;
      refLng = coords.dropoffLng;
      continue;
    }

    let distanceMiles;
    const isMultiload = trip.is_multiload && trip.multiload_group_id;
    const groupId = trip.multiload_group_id;

    if (isMultiload && multiloadGroups[groupId]) {
      // 🚛 MULTILOAD TRIP: Calculate deadhead based on position in group
      const groupTrips = multiloadGroups[groupId];
      const tripIndex = groupTrips.findIndex(t => 
        (t.id || t.trip_id) === (trip.id || trip.trip_id)
      );

      if (tripIndex === 0) {
        // First trip in multiload group: deadhead from previous location
        distanceMiles = haversineDistance(
          refLat,
          refLng,
          coords.pickupLat,
          coords.pickupLng
        );
      } else {
        // Subsequent trips in multiload: leg from PREVIOUS PICKUP (P1→P2, P2→P3) for card display
        const prevTrip = groupTrips[tripIndex - 1];
        const prevCoords = getTripCoords(prevTrip);
        if (prevCoords) {
          distanceMiles = haversineDistance(
            prevCoords.pickupLat,
            prevCoords.pickupLng,
            coords.pickupLat,
            coords.pickupLng
          );
        } else {
          distanceMiles = 0;
        }
      }

      // Update refLat/refLng ONLY after the last trip in the multiload group.
      // IMPORTANT: Use the *actual last dropoff* from multiloadRoutes when available (e.g. P1 P2 D2 D1 => end is D1).
      if (tripIndex === groupTrips.length - 1) {
        let endRefCoords = coords; // fallback: this trip's dropoff
        const steps = multiloadRoutes && groupId ? multiloadRoutes[groupId] : null;
        if (Array.isArray(steps) && steps.length) {
          let lastDropTripId = null;
          for (const s of steps) {
            if (s?.stop_type === 'dropoff' && s.trip_id != null) lastDropTripId = String(s.trip_id);
          }
          if (lastDropTripId) {
            const endTrip = groupTrips.find((t) => String(t.trip_id || t.id || '') === lastDropTripId);
            const endTripCoords = endTrip ? getTripCoords(endTrip) : null;
            if (endTripCoords) endRefCoords = endTripCoords;
          }
        }
        refLat = endRefCoords.dropoffLat;
        refLng = endRefCoords.dropoffLng;
      }
    } else {
      // 🚗 SINGLE TRIP (not multiload): Calculate deadhead from previous location
      distanceMiles = haversineDistance(
        refLat,
        refLng,
        coords.pickupLat,
        coords.pickupLng
      );

      // Update ref location to this trip's dropoff (for next trip)
      refLat = coords.dropoffLat;
      refLng = coords.dropoffLng;
    }

    const etaMinutes = distanceToEtaMinutes(distanceMiles);

    trip.deadhead_miles = Math.round(distanceMiles * 100) / 100;
    trip.eta_to_pickup_minutes = Math.round(etaMinutes * 100) / 100;

    // So Total Miles includes this trip when it was missing (e.g. dragged from unassigned)
    const tripDist =
      Number(trip.trip_distance_miles) || Number(trip.trip_distance) || 0;
    if (!tripDist) {
      const dist = haversineDistance(
        coords.pickupLat,
        coords.pickupLng,
        coords.dropoffLat,
        coords.dropoffLng
      );
      trip.trip_distance_miles = Math.round(dist * 100) / 100;
    }
  }

  return trips;
}

/**
 * Whether this trip's deadhead_miles should count in driver/header total DH.
 * Multiload P2/P3… still show leg miles on the card, but only P1 (empty vehicle) counts in total.
 * @param {object} trip
 * @returns {boolean}
 */
export function countsTowardTotalDeadhead(trip) {
  if (!trip || trip.is_break || trip.is_busy_trip) return false;
  if (!trip.is_multiload || !trip.multiload_group_id) return true;
  const order = Number(trip.multiload_pickup_order);
  if (!Number.isNaN(order) && order > 0) return order === 1;
  if (trip.is_multiload_primary === true) return true;
  if (trip.is_multiload_primary === false) return false;
  return true;
}

/**
 * Sum deadhead for driver totals / DH badges (excludes subsequent multiload pickups).
 * @param {object[]} trips
 * @returns {number}
 */
export function sumDeadheadMilesForTotal(trips) {
  if (!trips?.length) return 0;
  return trips.reduce((sum, t) => {
    if (!countsTowardTotalDeadhead(t)) return sum;
    return sum + (Number(t.deadhead_miles) || 0);
  }, 0);
}

/**
 * Ensure a trip has canonical coord fields (pickup_lat/lng, dropoff_lat/lng) so recompute and header logic can read them.
 * Mutates the trip in place if coords are found under any supported key.
 * @param {object} trip
 * @returns {boolean} true if coords were set
 */
export function normalizeTripCoords(trip) {
  if (!trip) return false;
  const coords = getTripCoords(trip);
  if (!coords) return false;
  trip.pickup_lat = coords.pickupLat;
  trip.pickup_lng = coords.pickupLng;
  trip.dropoff_lat = coords.dropoffLat;
  trip.dropoff_lng = coords.dropoffLng;
  return true;
}
